import { useEffect, useState } from 'react';
import { Input, InputNumber, Upload } from 'antd';
import { v4 as uuid } from 'uuid';
import { XSign } from 'assets/images/components/NudosComponents';
import { DeleteIcon, UploadIcon } from 'assets/images/countryList';
import { ACCEPTED_FILE_EXTENSION } from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import { NudosButton, NudosGenericModal, NudosModalContent } from 'components/NudosComponents';
import useDebounce from 'hooks/useDebounce';
import { checkCurrencyAvailability, createCurrencyWithExchange } from 'services/organization/currency.service';
import { TAny } from 'types/blog';
import { nudosGray400 } from 'styles/styles.constants';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { getFileExtension } from '../countriesTab/countriesTab.utils';
import {
  CURRENCY_CODE_MAX_LENGTH,
  getAddCurrencyFormValidation,
  shouldCheckCurrencyCodeAvailability,
  shouldCheckCurrencyNameAvailability
} from 'utils/currencyTable.utils';

import './addCurrencyModal.scss';

export interface ICreatedCurrency {
  code: string;
  name: string;
  exchangeRate: number | null;
}

interface IAddCurrencyModalForm {
  onClose: () => void;
  onCurrencyCreated: (createdCurrency?: ICreatedCurrency) => void | Promise<void>;
  initialCurrencyName?: string;
}

/** Modal con formulario de creación de moneda. */
const AddCurrencyModalForm = ({ onClose, onCurrencyCreated, initialCurrencyName }: IAddCurrencyModalForm) => {
  const [currencyCode, setCurrencyCode] = useState('');
  const [currencyName, setCurrencyName] = useState(initialCurrencyName?.trim() ?? '');
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [flagFile, setFlagFile] = useState<File | undefined>();
  const [codeExists, setCodeExists] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const debouncedCurrencyCode = useDebounce(currencyCode, 800);
  const debouncedCurrencyName = useDebounce(currencyName, 800);

  const isCheckingAvailability = isCheckingCode || isCheckingName;
  const { codeErrorText, nameErrorText, hasFilledCode, hasFilledName, hasFilledExchangeRate, isSubmitEnabled } =
    getAddCurrencyFormValidation({
      currencyCode,
      currencyName,
      exchangeRate,
      codeExists,
      nameExists,
      isCheckingAvailability,
      isSaving
    });

  /** Normaliza el código de moneda a mayúsculas y limita su longitud. */
  const handleCurrencyCodeChange = (value: string) => {
    const normalizedValue = value.toUpperCase().replace(/[^A-Z]/g, '');
    setCurrencyCode(normalizedValue.slice(0, CURRENCY_CODE_MAX_LENGTH));
  };

  /** Consulta la disponibilidad del código de moneda y actualiza el estado del formulario. */
  const validateCodeAvailability = async (code: string) => {
    if (!shouldCheckCurrencyCodeAvailability(code)) {
      setCodeExists(false);
      setIsCheckingCode(false);
      return;
    }

    setIsCheckingCode(true);

    try {
      const { codeExists: exists } = await checkCurrencyAvailability({ code: code.trim() });
      setCodeExists(exists);
    } catch {
      displayErrorNotification();
    } finally {
      setIsCheckingCode(false);
    }
  };

  /** Consulta la disponibilidad del nombre de moneda y actualiza el estado del formulario. */
  const validateNameAvailability = async (name: string) => {
    if (!shouldCheckCurrencyNameAvailability(name)) {
      setNameExists(false);
      setIsCheckingName(false);
      return;
    }

    setIsCheckingName(true);

    try {
      const { nameExists: exists } = await checkCurrencyAvailability({ name: name.trim() });
      setNameExists(exists);
    } catch {
      displayErrorNotification();
    } finally {
      setIsCheckingName(false);
    }
  };

  /** Procesa la carga de la bandera permitiendo solo archivos PNG. */
  const handleFlagUpload = (file: TAny) => {
    const extension = getFileExtension(file?.name ?? '');

    if (extension !== ACCEPTED_FILE_EXTENSION.PNG) {
      displayErrorNotification({ customJSXMessage: <>Solo puedes subir archivos PNG</> });
      return;
    }

    setFlagFile(file);
  };

  /** Crea la moneda con su tasa de cambio inicial. */
  const handleCreateCurrency = async () => {
    if (!isSubmitEnabled || exchangeRate === null) {
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      const payload = {
        code: currencyCode.trim().toUpperCase(),
        name: currencyName.trim(),
        exchangeRate
      };

      if (flagFile) {
        const extension = getFileExtension(flagFile.name);
        formData.append('file', flagFile, `currency-flag-${uuid()}${extension}`);
      }

      formData.append('data', JSON.stringify(payload));

      await createCurrencyWithExchange(formData);
      await onCurrencyCreated(payload);
      displaySuccessNotification({
        customJSXMessage: <>La moneda se creó correctamente.</>
      });
      onClose();
    } catch {
      displayErrorNotification();
    } finally {
      setIsSaving(false);
    }
  };

  /** Dispara la validación de disponibilidad del código cuando cambia el valor con debounce. */
  useEffect(() => {
    validateCodeAvailability(debouncedCurrencyCode);
  }, [debouncedCurrencyCode]);

  /** Dispara la validación de disponibilidad del nombre cuando cambia el valor con debounce. */
  useEffect(() => {
    validateNameAvailability(debouncedCurrencyName);
  }, [debouncedCurrencyName]);

  return (
    <NudosGenericModal
      customClassName="addCurrencyModal"
      closeModalCallback={onClose}
      clickOutsideCallback={onClose}
      modalContent={
        <NudosModalContent
          componentSize="medium"
          excludeHeader
          isSectionContainerFull
          customHeight="auto"
          CustomModalComponent={
            <div className="addCurrencyModalLayout">
              <button type="button" className="addCurrencyModalClose" onClick={onClose} aria-label="Cerrar modal">
                <XSign fill={nudosGray400} />
              </button>

              <div className="addCurrencyModalInfo">
                <h2 className="addCurrencyModalTitle">Nueva moneda</h2>
                <p className="addCurrencyModalSubtitle">Crea una nueva moneda y establece su tasa de cambio</p>

                <div className="addCurrencyModalForm">
                  <div className="flagUploadSection">
                    <div className={`fieldLabel ${flagFile ? 'fieldLabelFilled' : ''}`}>Bandera</div>
                    <div className="flagUploadBox">
                      {flagFile ? (
                        <button type="button" className="flagUploadFilled" onClick={() => setFlagFile(undefined)}>
                          <DeleteIcon />
                        </button>
                      ) : (
                        <Upload
                          accept={ACCEPTED_FILE_EXTENSION.PNG}
                          customRequest={event => handleFlagUpload(event.file)}
                          showUploadList={false}
                        >
                          <div className="flagUploadEmpty">
                            <UploadIcon />
                          </div>
                        </Upload>
                      )}
                    </div>
                  </div>

                  <div className="currencyFieldsRow">
                    <div className="currencyField">
                      <div className={`fieldLabel ${hasFilledCode ? 'fieldLabelFilled' : ''}`}>Código de la moneda</div>
                      <Input
                        className="currencyFieldInput"
                        placeholder="Código"
                        value={currencyCode}
                        maxLength={CURRENCY_CODE_MAX_LENGTH}
                        onChange={event => handleCurrencyCodeChange(event.target.value)}
                      />
                      {codeErrorText && <div className="fieldErrorText">{codeErrorText}</div>}
                    </div>

                    <div className="currencyField">
                      <div className={`fieldLabel ${hasFilledName ? 'fieldLabelFilled' : ''}`}>Nombre de la moneda</div>
                      <Input
                        className="currencyFieldInput"
                        placeholder="Nombre de moneda"
                        value={currencyName}
                        onChange={event => setCurrencyName(event.target.value)}
                      />
                      {nameErrorText && <div className="fieldErrorText">{nameErrorText}</div>}
                    </div>
                  </div>

                  <div className="currencyField exchangeRateField">
                    <div className={`fieldLabel ${hasFilledExchangeRate ? 'fieldLabelFilled' : ''}`}>
                      Tasa de cambio
                    </div>
                    <InputNumber
                      className="exchangeRateFieldInput"
                      placeholder="0"
                      value={exchangeRate ?? undefined}
                      onChange={value => setExchangeRate(value as number | null)}
                    />
                  </div>
                </div>
              </div>

              <div className="addCurrencyModalFooter">
                <NudosButton
                  buttonText={isSaving ? 'Loading' : 'Agregar moneda'}
                  handleClick={handleCreateCurrency}
                  isButtonDisabled={!isSubmitEnabled}
                  isButtonLoading={isSaving}
                  customWidth="20rem"
                  customHeight="3.8rem"
                />
              </div>
            </div>
          }
        />
      }
    />
  );
};

export default AddCurrencyModalForm;
